/**
 * Presentation for the frozen transfer status set.
 *
 * PHASE_01_PRODUCTION_ARCHITECTURE.md §12 defines the transfer machine as
 * bank-authoritative: "Nura originates zero transitions. Its mirror is a lossy
 * projection marked non-authoritative." The copy below therefore never claims
 * Nura decided anything — a declined transfer was declined by the bank, and a
 * settled one was settled by the bank.
 *
 * ## Why this union is redeclared rather than imported
 *
 * `backend/shared/transfer-status/` documents itself as "an internal import
 * surface for Nura backend code". Client code importing it would create exactly
 * the backend-to-client coupling the Phase 03 early-foundation decision keeps
 * out. Instead the vocabulary is restated here for presentation, and
 * `frontend/test/status.test.ts` asserts — from test code, which may import
 * both — that this set is identical to the frozen `TRANSFER_STATUSES`. Drift
 * fails the build instead of reaching a screen.
 */

import type { IconName } from "../components/icon.ts";

/** The §12 server-side transfer statuses, in graph order. */
export const TRANSFER_STATUS_ORDER = [
  "AUTHORIZATION_REQUESTED",
  "AUTHORIZING",
  "AUTHORIZED_PENDING_CLAIM",
  "CLAIM_IN_PROGRESS",
  "CLAIMED",
  "SETTLED",
  "DENIED",
  "FAILED",
  "EXPIRED",
  "CANCELLED",
] as const;

export type TransferStatusName = (typeof TRANSFER_STATUS_ORDER)[number];

/** Visual tone. Tone is never the only signal — label and icon always accompany it. */
export type StatusTone = "neutral" | "progress" | "success" | "attention" | "danger";

export interface StatusPresentation {
  readonly label: string;
  readonly tone: StatusTone;
  readonly icon: IconName;
  /** One sentence a person can act on, naming the responsible party. */
  readonly detail: string;
}

const PRESENTATION: Readonly<Record<TransferStatusName, StatusPresentation>> = {
  AUTHORIZATION_REQUESTED: {
    label: "Requested",
    tone: "progress",
    icon: "clock",
    detail: "Sent to your bank for approval.",
  },
  AUTHORIZING: {
    label: "Awaiting approval",
    tone: "progress",
    icon: "clock",
    detail: "Your bank is reviewing this transfer.",
  },
  AUTHORIZED_PENDING_CLAIM: {
    label: "Ready to claim",
    tone: "success",
    icon: "check-circle",
    detail: "Your bank approved this transfer and is holding it for the recipient.",
  },
  CLAIM_IN_PROGRESS: {
    label: "Being claimed",
    tone: "progress",
    icon: "clock",
    detail: "The recipient has started claiming this transfer.",
  },
  CLAIMED: {
    label: "Claimed",
    tone: "success",
    icon: "check-circle",
    detail: "The recipient claimed this transfer. Their bank is completing it.",
  },
  SETTLED: {
    label: "Settled",
    tone: "success",
    icon: "check-circle",
    detail: "The banks completed this transfer.",
  },
  DENIED: {
    label: "Declined",
    tone: "danger",
    icon: "x-circle",
    detail: "Your bank declined this transfer. Your bank can tell you why.",
  },
  FAILED: {
    label: "Failed",
    tone: "danger",
    icon: "x-circle",
    detail: "This transfer could not be completed. No money left your account.",
  },
  EXPIRED: {
    label: "Expired",
    tone: "attention",
    icon: "warning",
    detail: "The claim window closed before the recipient claimed it.",
  },
  CANCELLED: {
    label: "Cancelled",
    tone: "neutral",
    icon: "prohibit",
    detail: "This transfer was cancelled.",
  },
};

export function statusPresentation(status: TransferStatusName): StatusPresentation {
  return PRESENTATION[status];
}
